import axios from 'axios';
import { refreshFirebaseTokenIfNeeded, forceRefreshFirebaseToken, hasActiveImport } from '../utils/tokenRefresh';

// Global loading state management
let loadingCount = 0;
let setGlobalLoading = null;

export const setLoadingProvider = (setLoadingFn) => {
  setGlobalLoading = setLoadingFn;
};

const updateLoading = (increment, message = 'Loading...') => {
  if (setGlobalLoading) {
    loadingCount += increment;
    setGlobalLoading(loadingCount > 0, message);
  }
};

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5005/api',
  timeout: 300000, // 5 minutes for large simulations (increased from 10 seconds)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token, refresh if needed, and show loading
api.interceptors.request.use(
  async (config) => {
    // Check if token is a Firebase token (longer than 200 chars)
    const token = localStorage.getItem('token');
    const firebaseToken = localStorage.getItem('firebaseToken');
    const currentToken = firebaseToken || token;
    
    if (currentToken && currentToken.length > 200) {
      // Firebase token - refresh if needed before request
      try {
        const refreshedToken = await refreshFirebaseTokenIfNeeded();
        if (refreshedToken) {
          config.headers.Authorization = `Bearer ${refreshedToken}`;
        } else if (currentToken) {
          config.headers.Authorization = `Bearer ${currentToken}`;
        }
      } catch (error) {
        console.error('Error refreshing token in request interceptor:', error);
        // Continue with current token if refresh fails
        if (currentToken) {
          config.headers.Authorization = `Bearer ${currentToken}`;
        }
      }
    } else if (token) {
      // JWT token - use as is
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Show loading for API calls
    updateLoading(1, 'Loading...');
    
    return config;
  },
  (error) => {
    updateLoading(-1);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors and hide loading
api.interceptors.response.use(
  (response) => {
    updateLoading(-1);
    return response;
  },
  async (error) => {
    updateLoading(-1);
    
    if (error.response?.status === 401) {
      const originalRequest = error.config;
      
      // Check if this is a retry attempt (to avoid infinite loops)
      if (originalRequest._retry) {
        // Already retried, logout user
        localStorage.removeItem('token');
        localStorage.removeItem('firebaseToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(error);
      }
      
      // Check if there's an active import - handle gracefully
      if (hasActiveImport()) {
        console.log('401 error during active import, attempting token refresh...');
        
        // Try to refresh token and retry the request
        try {
          const refreshedToken = await forceRefreshFirebaseToken();
          if (refreshedToken) {
            originalRequest._retry = true;
            originalRequest.headers.Authorization = `Bearer ${refreshedToken}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          console.error('Failed to refresh token during active import:', refreshError);
          // Don't logout during active import - let the polling handle it
          return Promise.reject(error);
        }
      }
      
      // For non-import requests or if refresh failed, check if we can refresh
      const token = localStorage.getItem('token');
      const firebaseToken = localStorage.getItem('firebaseToken');
      const currentToken = firebaseToken || token;
      
      if (currentToken && currentToken.length > 200) {
        // Firebase token - try to refresh once
        try {
          const refreshedToken = await forceRefreshFirebaseToken();
          if (refreshedToken) {
            originalRequest._retry = true;
            originalRequest.headers.Authorization = `Bearer ${refreshedToken}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          console.error('Failed to refresh token:', refreshError);
        }
      }
      
      // If refresh failed or not a Firebase token, logout
      localStorage.removeItem('token');
      localStorage.removeItem('firebaseToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api; 